import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";

const Footer = () => {
  const { t, locale } = useI18n();
  const base = `/${locale}`;

  return (
    <footer
      className="bg-[#0B192C] text-[#CBD5E1] border-t border-[#1E293B]"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center justify-center h-8 w-8 border border-[#C85A32]">
                <span className="font-serif font-bold text-[#C85A32] text-lg leading-none">V</span>
              </span>
              <span className="font-serif font-bold text-white text-lg tracking-tight">Voryzix</span>
            </div>
            <p className="text-sm leading-relaxed text-[#94A3B8]" data-testid="footer-tagline">
              {t("footer.tagline")}
            </p>
          </div>

          {/* Platform */}
          <div>
            <h4 className="text-xs font-mono uppercase tracking-widest text-[#C85A32] mb-4">
              {t("footer.columns.platform.title")}
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a href={`${base}#sectors`} className="hover:text-white" data-testid="footer-link-sectors">
                  {t("footer.columns.platform.links.sectors")}
                </a>
              </li>
              <li>
                <a href={`${base}#methodology`} className="hover:text-white" data-testid="footer-link-methodology">
                  {t("footer.columns.platform.links.methodology")}
                </a>
              </li>
              <li>
                <a href={`${base}#transparency`} className="hover:text-white" data-testid="footer-link-transparency">
                  {t("footer.columns.platform.links.transparency")}
                </a>
              </li>
              <li>
                <a href={`${base}#newsletter`} className="hover:text-white" data-testid="footer-link-newsletter">
                  {t("footer.columns.platform.links.newsletter")}
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-xs font-mono uppercase tracking-widest text-[#C85A32] mb-4">
              {t("footer.columns.legal.title")}
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to={`${base}/privacy`} className="hover:text-white" data-testid="footer-privacy-link">
                  {t("footer.columns.legal.links.privacy")}
                </Link>
              </li>
              <li>
                <Link to={`${base}/cookies`} className="hover:text-white" data-testid="footer-cookies-link">
                  {t("footer.columns.legal.links.cookies")}
                </Link>
              </li>
              <li>
                <Link to={`${base}/terms`} className="hover:text-white" data-testid="footer-terms-link">
                  {t("footer.columns.legal.links.terms")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs font-mono uppercase tracking-widest text-[#C85A32] mb-4">
              {t("footer.columns.contact.title")}
            </h4>
            <ul className="space-y-3 text-sm">
              <li>
                <div className="text-[#94A3B8] text-xs uppercase tracking-wider">
                  {t("footer.columns.contact.email_label")}
                </div>
                <a
                  href={`mailto:${t("footer.columns.contact.email")}`}
                  className="hover:text-white font-mono"
                  data-testid="footer-contact-email"
                >
                  {t("footer.columns.contact.email")}
                </a>
              </li>
              <li>
                <div className="text-[#94A3B8] text-xs uppercase tracking-wider">
                  {t("footer.columns.contact.ir_label")}
                </div>
                <a
                  href={`mailto:${t("footer.columns.contact.ir_email")}`}
                  className="hover:text-white font-mono"
                  data-testid="footer-ir-email"
                >
                  {t("footer.columns.contact.ir_email")}
                </a>
              </li>
              <li>
                <a
                  href={`tel:${t("footer.columns.contact.phone").replace(/\s|\(|\)/g, "")}`}
                  className="hover:text-white font-mono"
                  data-testid="footer-phone"
                >
                  {t("footer.columns.contact.phone")}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Legal details */}
        <div className="mt-12 pt-8 border-t border-[#1E293B]">
          <h4 className="text-xs font-mono uppercase tracking-widest text-[#C85A32] mb-4">
            {t("footer.legal_details.title")}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-xs text-[#94A3B8]">
            <div data-testid="footer-legal-entity">{t("footer.legal_details.entity")}</div>
            <div data-testid="footer-legal-cuit">{t("footer.legal_details.cuit")}</div>
            <div data-testid="footer-legal-igj">{t("footer.legal_details.igj")}</div>
            <div data-testid="footer-legal-license">{t("footer.legal_details.license")}</div>
            <div data-testid="footer-legal-founded">{t("footer.legal_details.founded")}</div>
            <div data-testid="footer-legal-jurisdiction">{t("footer.legal_details.jurisdiction")}</div>
            <div className="md:col-span-2 lg:col-span-3" data-testid="footer-legal-address">
              {t("footer.legal_details.address")}
            </div>
          </div>
          <p className="mt-6 text-xs leading-relaxed text-[#94A3B8]" data-testid="footer-disclaimer">
            {t("footer.disclaimer")}
          </p>
          <p className="mt-6 text-xs text-[#64748B]" data-testid="footer-copyright">
            {t("footer.copyright")}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
